// src/pages/brand/BrandCreate.tsx
import React, { useMemo, useState, useCallback, useEffect, useRef, type ReactNode, FormEvent } from "react";
import { useLocation, useNavigate, useOutletContext } from "react-router-dom";
import { useAuth } from "../../context/AuthContext";

import { api, isApiError } from "../../lib/api";

import { defaultBrandKit, type BrandKit, type BrandModeCreationType } from "../../types/brand";
import { getBrandPreset, BRAND_PRESETS_LIST } from "../../config/brandPresets";

import type { PlanKey, ResolutionKey } from "../../config/plans";
import {
  canPersistBrandKit,
  isResolutionAllowed,
  getMinPlanForResolution,
  getBrandKitsLimit,
  BRAND_LIMIT_UNLIMITED,
  costCreditsForResolution,
  getPlanConfig,
} from "../../config/plans";

import { RESOLUTIONS_CONFIG } from "../../config/qualityCost";

import { useBrandStore } from "../../hooks/useBrandStore";
import type { DashboardStyle } from "../../utils/brandHandoff";
import UpgradeModal from "../../components/UpgradeModal";

// ✅ Fuente única de verdad (plan/créditos) desde AppShell
import type { ModeShellContext } from "../../components/layout/AppShell";

function useQuery() {
  const { search } = useLocation();
  return useMemo(() => new URLSearchParams(search), [search]);
}

function prettyPlan(plan: PlanKey) {
  if (plan === "free") return "Free";
  if (plan === "basic") return "Basic";
  if (plan === "pro") return "Pro";
  return "Premium";
}

function yesNo(v: boolean) {
  return v ? "✅" : "❌";
}

// Mapper: BrandKit.visualStyle -> Dashboard style
function mapVisualStyleToDashboardStyle(v: string): DashboardStyle {
  const s = String(v || "").toLowerCase();
  if (s.includes("minimal")) return "Minimal";
  if (s.includes("illustr")) return "Ilustración";
  if (s.includes("anime")) return "Anime";
  if (s.includes("vintage")) return "Vintage";
  if (s.includes("logo")) return "Logo corporativo";
  if (s.includes("epic") || s.includes("portada")) return "Portada épica";
  if (s.includes("futur") || s.includes("3d") || s.includes("cyber")) return "Futurista";
  return "Realista";
}

/** ─────────────────────────────────────────────────────────────
 * ✅ Proyectos (backend) – usamos /api/projects
 * ───────────────────────────────────────────────────────────── */
type Project = { id: string; name: string; createdAt: string };

type ProjectsResponse = {
  success: boolean;
  projects?: Array<{ id: string; name: string; created_at: string }>;
  project?: { id: string; name: string; created_at: string };
  error?: string;
};

/** ─────────────────────────────────────────────────────────────
 * ✅ Campañas (localStorage) – Pro/Premium
 * ───────────────────────────────────────────────────────────── */
type Campaign = { id: string; name: string; createdAt: string };

function cryptoId() {
  return `${Date.now()}_${Math.random().toString(16).slice(2)}`;
}

function campaignsKey(userKey: string, projectId: string) {
  return `ovylon_brand_campaigns_${userKey}_${projectId}`;
}

function readCampaigns(userKey: string, projectId: string): Campaign[] {
  try {
    const raw = localStorage.getItem(campaignsKey(userKey, projectId));
    if (!raw) return [];
    const parsed = JSON.parse(raw) as any[];
    if (!Array.isArray(parsed)) return [];
    return parsed
      .map((c) => ({
        id: String(c?.id || ""),
        name: String(c?.name || ""),
        createdAt: String(c?.createdAt || ""),
      }))
      .filter((c) => c.id && c.name);
  } catch {
    return [];
  }
}

function writeCampaigns(userKey: string, projectId: string, items: Campaign[]) {
  try {
    localStorage.setItem(campaignsKey(userKey, projectId), JSON.stringify(items));
  } catch {
    // ignore
  }
}

/** ─────────────────────────────────────────────────────────────
 * ✅ UX helpers (igual que Dashboard)
 * ───────────────────────────────────────────────────────────── */
function resolutionSizeHint(res: ResolutionKey): string {
  if (res === "720p") return "1280×720";
  if (res === "1080p") return "1920×1080";
  if (res === "2k") return "2048×1152";
  if (res === "4k") return "3840×2160";
  if (res === "8k") return "7680×4320";
  return "";
}

function getExportSizeLabel(res: ResolutionKey) {
  const cfg = (RESOLUTIONS_CONFIG as any)?.[res];
  if (cfg?.width && cfg?.height) return `${cfg.width}×${cfg.height}`;
  return resolutionSizeHint(res) || "—";
}

function needsUpscaleToBeReal(res: ResolutionKey) {
  return res === "2k" || res === "4k" || res === "8k";
}

function normalizeResolutionKey(v?: string | null): ResolutionKey | null {
  const s = String(v || "").toLowerCase().trim();
  if (s === "720p") return "720p";
  if (s === "1080p") return "1080p";
  if (s === "2k") return "2k";
  if (s === "4k") return "4k";
  if (s === "8k") return "8k";
  return null;
}

function prettyEngine(engine?: string | null) {
  const v = String(engine || "").toLowerCase();
  if (!v) return "—";
  if (v.includes("replicate_upscale")) return "Upscaled (Real-ESRGAN)";
  if (v.includes("replicate")) return "Replicate";
  if (v.includes("openai")) return "OpenAI";
  if (v.includes("upscale")) return "Upscaled";
  return engine || "—";
}

/**
 * ✅ Sanitizer: nunca mostrar errores crudos como "maintenance_mode"
 */
function friendlyApiError(err?: unknown) {
  const raw = String(err || "").trim();
  if (!raw) return "Ocurrió un error. Intenta de nuevo.";
  if (raw === "maintenance_mode") return "El servidor está en mantenimiento. Intenta nuevamente en unos minutos.";
  if (raw === "rate_limited") return "Estamos recibiendo muchas solicitudes. Intenta de nuevo en unos segundos.";
  return raw;
}

/** ─────────────────────────────────────────────────────────────
 * ✅ UI tiny components (1:1 Dashboard)
 * ───────────────────────────────────────────────────────────── */
function PillLabel({ children }: { children: ReactNode }) {
  return <span className="text-[10px] text-gray-400 uppercase tracking-[0.18em]">{children}</span>;
}

function PillSelect(props: {
  value: string;
  onChange: (v: string) => void;
  disabled?: boolean;
  title?: string;
  options: Array<{ value: string; label: string; disabled?: boolean }>;
}) {
  return (
    <select
      value={props.value}
      disabled={props.disabled}
      title={props.title}
      onChange={(e) => props.onChange(e.target.value)}
      className="
        h-10 px-3 rounded-xl text-sm
        bg-[#0c0c14] hover:bg-[#121225]
        border border-white/15 hover:border-white/25
        text-gray-100 outline-none transition
        focus:ring-2 focus:ring-indigo-500/35 focus:border-indigo-400/40
        disabled:opacity-50 disabled:cursor-not-allowed
      "
    >
      {props.options.map((o) => (
        <option key={o.value} value={o.value} disabled={o.disabled} className="bg-[#0c0c14] text-gray-100">
          {o.label}
        </option>
      ))}
    </select>
  );
}

function NoisePlaceholder() {
  return (
    <div className="relative w-full h-full overflow-hidden">
      <div
        className="
          absolute inset-0
          bg-[radial-gradient(circle_at_20%_20%,rgba(255,255,255,0.10),transparent_35%),radial-gradient(circle_at_80%_30%,rgba(255,255,255,0.08),transparent_40%),radial-gradient(circle_at_50%_80%,rgba(255,255,255,0.06),transparent_45%),linear-gradient(135deg,rgba(99,102,241,0.18),rgba(168,85,247,0.12),rgba(15,23,42,0.35))]
          blur-[2px]
        "
      />
      <div className="absolute inset-0 bg-[linear-gradient(90deg,transparent,rgba(255,255,255,0.10),transparent)] animate-pulse opacity-40" />
      <div className="absolute inset-0 bg-black/30" />
    </div>
  );
}

/** ─────────────────────────────────────────────────────────────
 * ✅ Generations types (para historial 1:1)
 * ───────────────────────────────────────────────────────────── */
type IAResult = {
  id: string;
  prompt: string;
  style: string;
  imageUrl?: string;
  createdAt?: string;
  projectId?: string | null;
  campaignId?: string | null;
  resolution?: ResolutionKey | null;
  engine?: string | null;
  costCredits?: number | null;
  aspectRatio?: string | null;
};

type GenerateResponse = {
  success: boolean;
  imageUrl?: string;
  generationId?: string;
  newBalance?: number;
  resolution?: string;
  engine?: string | null;
  costCredits?: number | null;
  error?: string;
};

export const ALL_RESOLUTIONS: ResolutionKey[] = ["720p", "1080p", "2k", "4k", "8k"];

const ASPECT_RATIOS: Array<{ value: string; label: string }> = [
  { value: "1:1", label: "1:1" },
  { value: "4:3", label: "4:3" },
  { value: "16:9", label: "16:9" },
  { value: "21:9", label: "21:9" },
  { value: "5:4", label: "5:4" },
  { value: "3:2", label: "3:2" },
  { value: "2:3", label: "2:3" },
  { value: "9:16", label: "9:16" },
  { value: "3:4", label: "3:4" },
  { value: "4:5", label: "4:5" },
];

export default function BrandCreate() {
  const navigate = useNavigate();
  const query = useQuery();
  const location = useLocation();
  const { user, getAccessToken } = useAuth();

  // ✅ Fuente única desde AppShell
  const outlet = useOutletContext<ModeShellContext>();
  const userPlan: PlanKey = outlet?.mePlan || "free";
  const credits = outlet?.credits ?? null;
  const isPreview = !!outlet?.isPreview || !user;

  const allowPersist = canPersistBrandKit(userPlan);
  const allowAdvanced = userPlan === "pro" || userPlan === "premium";

  // ✅ plan config para allowedResolutions (como Dashboard)
  const planConfig = useMemo(() => getPlanConfig(userPlan), [userPlan]);
  const allowedResolutions = planConfig.allowedResolutions;

  // ✅ preset
  const presetParam = query.get("preset");
  const hasPreset = !!presetParam;

  const presetId = (presetParam || "") as BrandModeCreationType;
  const preset = hasPreset ? getBrandPreset(presetId) : null;

  const view: "selector" | "invalid" | "form" = !hasPreset ? "selector" : preset ? "form" : "invalid";

  const userKey = user ? user.email || user.id : null;
  const { activeBrand } = useBrandStore(userKey, userPlan);

  // ✅ Brands limit label
  const brandsLimitLabel = useMemo(() => {
    const lim = getBrandKitsLimit(userPlan);
    return lim >= BRAND_LIMIT_UNLIMITED ? "Ilimitadas" : String(lim);
  }, [userPlan]);

  // brief (Brand-specific)
  const [goal, setGoal] = useState("");
  const [productOrTopic, setProductOrTopic] = useState("");
  const [audience, setAudience] = useState("");

  // ✅ Proyectos UI
  const [projects, setProjects] = useState<Project[]>([]);
  const [projectsLoading, setProjectsLoading] = useState(false);
  const [projectsError, setProjectsError] = useState("");

  const [selectedProjectId, setSelectedProjectId] = useState<string>("none");

  // ✅ Campañas
  const [campaigns, setCampaigns] = useState<Campaign[]>([]);
  const [selectedCampaignId, setSelectedCampaignId] = useState<string>("none");

  // Modal crear proyecto
  const [createProjectOpen, setCreateProjectOpen] = useState(false);
  const [projectName, setProjectName] = useState("");

  // Modal crear campaña
  const [createCampaignOpen, setCreateCampaignOpen] = useState(false);
  const [campaignName, setCampaignName] = useState("");

  // ✅ Modal upgrade
  const [upgradeOpen, setUpgradeOpen] = useState(false);
  const [upgradeCfg, setUpgradeCfg] = useState<{
    title: string;
    requiredPlan: PlanKey;
    feature: string;
    bullets: string[];
    from: string;
  } | null>(null);

  const goLoginWithNext = useCallback(
    (nextPath: string) => {
      const next = encodeURIComponent(nextPath);
      navigate(`/login?next=${next}`, { replace: false });
    },
    [navigate]
  );

  const openUpgrade = useCallback(
    (cfg: { title: string; requiredPlan: PlanKey; feature: string; bullets: string[]; from: string }) => {
      if (isPreview || !user) {
        goLoginWithNext(cfg.from || location.pathname + (location.search || ""));
        return;
      }
      setUpgradeCfg(cfg);
      setUpgradeOpen(true);
    },
    [isPreview, user, goLoginWithNext, location.pathname, location.search]
  );

  const openUpgradePersist = () => {
    openUpgrade({
      title: "Guardar consistencia (Brand Kit)",
      requiredPlan: "basic",
      feature: "brand_persist",
      bullets: ["Guardar y reutilizar colores / estilo / tono", "Consistencia real en campañas completas", "Menos trabajo manual"],
      from: hasPreset ? `/brand/create?preset=${presetId}` : "/brand/create",
    });
  };

  const openUpgradeAdvanced = () => {
    openUpgrade({
      title: "Desbloquear campañas (subniveles) + Advanced",
      requiredPlan: "pro",
      feature: "brand_campaigns",
      bullets: ["Campañas dentro de proyectos", "Organiza por semana/formato/ads", "Ideal freelancers y equipos"],
      from: hasPreset ? `/brand/create?preset=${presetId}` : "/brand/create",
    });
  };

  const goEditBrandKit = () => {
    if (!user) {
      goLoginWithNext(hasPreset ? `/brand/create?preset=${presetId}` : "/brand/create");
      return;
    }
    if (!allowPersist) {
      openUpgradePersist();
      return;
    }
    navigate("/brand/kit");
  };

  // kit efectivo
  const effectiveName = useMemo(() => (activeBrand?.name || "").trim() || "Mi marca", [activeBrand]);
  const effectiveDescription = useMemo(() => (activeBrand?.description || "").trim(), [activeBrand]);

  const effectiveKit: BrandKit = useMemo(() => {
    if (!allowPersist) return defaultBrandKit();
    return activeBrand?.kit ?? defaultBrandKit();
  }, [activeBrand, allowPersist]);

  // ✅ resolución default + state (como Dashboard)
  const defaultResolution: ResolutionKey = useMemo(() => {
    const preferred: ResolutionKey = userPlan === "free" ? "720p" : "1080p";
    if (allowedResolutions.includes(preferred)) return preferred;
    return allowedResolutions[0] ?? "720p";
  }, [userPlan, allowedResolutions]);

  const [resolution, setResolution] = useState<ResolutionKey>(defaultResolution);

  useEffect(() => {
    setResolution(defaultResolution);
  }, [defaultResolution]);

  // ✅ Si el plan cambia y la resolución actual ya no está permitida, ajustamos
  useEffect(() => {
    if (!allowedResolutions.includes(resolution)) {
      setResolution(allowedResolutions[0] ?? "720p");
    }
  }, [allowedResolutions, resolution]);

  // ✅ Aspect ratio fijo para presets tipo Banner / Ads (tamaños fijos)
  const fixedAspectRatio = useMemo(() => {
    if (!preset) return null;

    const id = String(presetId || "").toLowerCase();
    const title = String((preset as any)?.title || "").toLowerCase();

    const looksBanner = id.includes("banner") || title.includes("banner");
    const looksAds =
      id.includes("ads") || id.includes("ad") || title.includes("ads") || title.includes("anuncio") || title.includes("advert");

    if (looksBanner || looksAds) {
      return (preset as any)?.recommendedAspectRatio || "4:5";
    }
    return null;
  }, [preset, presetId]);

  // ─────────────────────────────────────────────
  // ✅ Load projects (backend)
  // ─────────────────────────────────────────────
  const loadProjects = useCallback(async () => {
    if (!user) return;

    setProjectsError("");
    setProjectsLoading(true);

    try {
      const token = await getAccessToken();
      if (!token) throw new Error("No hay sesión activa.");

      const resp = await fetch("/api/projects", {
        method: "GET",
        headers: { Authorization: `Bearer ${token}` },
      });

      const data = (await resp.json()) as ProjectsResponse;

      if (!resp.ok || !data?.success || !Array.isArray(data.projects)) {
        throw new Error(data?.error || "No se pudieron cargar los proyectos.");
      }

      const mapped: Project[] = data.projects.map((p) => ({
        id: p.id,
        name: p.name,
        createdAt: p.created_at,
      }));

      setProjects(mapped);
    } catch (e) {
      setProjects([]);
      setProjectsError(e instanceof Error ? e.message : "Error cargando proyectos.");
    } finally {
      setProjectsLoading(false);
    }
  }, [user, getAccessToken]);

  useEffect(() => {
    if (!user) return;
    loadProjects();
  }, [user, loadProjects]);

  // ─────────────────────────────────────────────
  // ✅ Campaigns: localStorage when project changes
  // ─────────────────────────────────────────────
  useEffect(() => {
    if (!userKey) return;

    if (selectedProjectId === "none") {
      setCampaigns([]);
      setSelectedCampaignId("none");
      return;
    }

    const items = readCampaigns(userKey, selectedProjectId);
    setCampaigns(items);

    if (items.length === 0) {
      setSelectedCampaignId("none");
      return;
    }

    const stillExists = items.some((c) => c.id === selectedCampaignId);
    if (!stillExists) setSelectedCampaignId("none");
  }, [selectedProjectId, userKey, selectedCampaignId]);

  // ─────────────────────────────────────────────
  // ✅ Create project (backend)
  // ─────────────────────────────────────────────
  const createProject = useCallback(async () => {
    const name = projectName.trim();
    if (!name) return;

    if (!user) {
      goLoginWithNext("/brand/create");
      return;
    }

    try {
      const token = await getAccessToken();
      if (!token) throw new Error("No hay sesión activa.");

      const resp = await fetch("/api/projects", {
        method: "POST",
        headers: { "Content-Type": "application/json", Authorization: `Bearer ${token}` },
        body: JSON.stringify({ name, sourceMode: "brand" }),
      });

      const data = (await resp.json()) as ProjectsResponse;

      if (!resp.ok || !data?.success || !data.project) {
        throw new Error(data?.error || "No se pudo crear el proyecto.");
      }

      const p: Project = {
        id: data.project.id,
        name: data.project.name,
        createdAt: data.project.created_at,
      };

      setProjects((prev) => [p, ...prev]);
      setSelectedProjectId(p.id);
      setSelectedCampaignId("none");
      setProjectName("");
      setCreateProjectOpen(false);
    } catch (e) {
      alert(e instanceof Error ? e.message : "Error creando proyecto.");
    }
  }, [projectName, user, getAccessToken, goLoginWithNext]);

  // ─────────────────────────────────────────────
  // ✅ Create campaign (localStorage) – Pro/Premium
  // ─────────────────────────────────────────────
  const createCampaign = useCallback(() => {
    if (!userKey) return;
    if (!allowAdvanced) return;
    if (selectedProjectId === "none") return;

    const name = campaignName.trim();
    if (!name) return;

    const newItem: Campaign = {
      id: cryptoId(),
      name,
      createdAt: new Date().toISOString(),
    };

    const next = [newItem, ...campaigns];
    setCampaigns(next);
    setSelectedCampaignId(newItem.id);
    setCampaignName("");
    setCreateCampaignOpen(false);

    writeCampaigns(userKey, selectedProjectId, next);
  }, [userKey, allowAdvanced, selectedProjectId, campaignName, campaigns]);

  // ─────────────────────────────────────────────
  // ✅ Prompt base + prompt editable
  // ─────────────────────────────────────────────
  const canContinueBrief = !!goal.trim() && !!productOrTopic.trim() && !!audience.trim();

  const basePrompt = useMemo(() => {
    if (view !== "form" || !preset) return "";

    const brandName = effectiveName || "Mi marca";
    const notes = (effectiveKit.notes || "").trim();

    const base =
      `Brand: ${brandName}\n` +
      (effectiveDescription ? `Description: ${effectiveDescription}\n` : "") +
      `Creation: ${preset.title || presetId}\n` +
      `Goal: ${goal || "[define objetivo]"}\n` +
      `Product/Topic: ${productOrTopic || "[define producto/tema]"}\n` +
      `Audience: ${audience || "[define audiencia]"}\n`;

    let consistency = "";
    if (allowPersist) {
      const colors = (effectiveKit.colors || []).join(", ");
      consistency =
        `Brand consistency:\n` +
        `- Colors: ${colors || "N/A"}\n` +
        `- Visual style: ${effectiveKit.visualStyle}\n` +
        `- Tone: ${effectiveKit.tone}\n` +
        `- Image type: ${effectiveKit.imageType}\n`;
    }

    let adv = "";
    if (allowAdvanced) {
      const mood = (activeBrand as any)?.advanced?.mood ? String((activeBrand as any).advanced.mood).trim() : "";
      const ref = (activeBrand as any)?.advanced?.referenceUrl ? String((activeBrand as any).advanced.referenceUrl).trim() : "";
      if (mood) adv += `Mood: ${mood}\n`;
      if (ref) adv += `Reference URL: ${ref}\n`;
    }

    const extras = notes && allowPersist ? `Notes: ${notes}\n` : "";

    return (
      base +
      (consistency ? consistency : "") +
      (adv ? adv : "") +
      extras +
      `Output requirements:\n` +
      `- Make it business-ready (clean composition, high clarity).\n` +
      `- Avoid illegible text artifacts; keep text minimal or none unless requested.\n` +
      `- Aspect ratio suggestion: ${(preset as any).recommendedAspectRatio || "4:5"}\n`
    );
  }, [
    view,
    preset,
    presetId,
    effectiveName,
    effectiveDescription,
    effectiveKit,
    allowPersist,
    allowAdvanced,
    activeBrand,
    goal,
    productOrTopic,
    audience,
  ]);

  // ✅ prompt editable (NO autollenar)
  const [prompt, setPrompt] = useState("");
  const [style, setStyle] = useState<string>("Realista");
  const [aspectRatio, setAspectRatio] = useState("4:5");

  // sync defaults when preset changes
  useEffect(() => {
    if (view !== "form" || !preset) return;
    setPrompt("");
    const mappedStyle = mapVisualStyleToDashboardStyle(effectiveKit.visualStyle);
    setStyle(mappedStyle);
    const ar = fixedAspectRatio || (preset as any).recommendedAspectRatio || "4:5";
    setAspectRatio(ar);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [presetId, view, fixedAspectRatio]);

  // ─────────────────────────────────────────────
  // ✅ HISTORIAL + PREVIEW
  // ─────────────────────────────────────────────
  const [isGenerating, setIsGenerating] = useState(false);

  const [results, setResults] = useState<IAResult[]>([]);
  const [historyLoading, setHistoryLoading] = useState(false);
  const [historyError, setHistoryError] = useState("");

  const [mainId, setMainId] = useState<string | null>(null);
  const mainItem = useMemo(() => {
    if (!mainId) return null;
    return results.find((r) => r.id === mainId) || null;
  }, [results, mainId]);

  // blur->sharp when main changes
  const [mainImgLoaded, setMainImgLoaded] = useState(false);
  const lastMainUrlRef = useRef<string | null>(null);
  useEffect(() => {
    const url = mainItem?.imageUrl || null;
    if (url !== lastMainUrlRef.current) {
      lastMainUrlRef.current = url;
      setMainImgLoaded(false);
    }
  }, [mainItem?.imageUrl]);

  // Preview modal
  const [previewOpen, setPreviewOpen] = useState(false);
  const [previewItem, setPreviewItem] = useState<IAResult | null>(null);

  const openPreview = useCallback((item: IAResult) => {
    setPreviewItem(item);
    setPreviewOpen(true);
  }, []);

  const closePreview = useCallback(() => {
    setPreviewOpen(false);
    setPreviewItem(null);
  }, []);

  const loadGenerations = useCallback(
  async (projectId?: string | "none") => {
    if (!user) return;

    setHistoryError("");
    setHistoryLoading(true);

    try {
      const token = await getAccessToken();
      if (!token) throw new Error("No hay sesión activa.");

      const pid = typeof projectId !== "undefined" ? projectId : selectedProjectId;
      const pidParam = pid && pid !== "none" ? pid : null;

      // ✅ Brand Studio SIEMPRE pide SOLO brand
      const data = await api.generationsList(
        { projectId: pidParam, sourceMode: "brand" },
        { headers: { Authorization: `Bearer ${token}` } }
      );

      if (!data?.success || !Array.isArray((data as any).items)) {
        throw new Error(friendlyApiError((data as any)?.error || "No se pudo cargar el historial."));
      }

      const mapped: IAResult[] = (data as any).items.map((g: any) => {
        const rk = normalizeResolutionKey(g.resolution);
        return {
          id: String(g.id),
          prompt: String(g.prompt || ""),
          style: String(g.style || "Realista"),
          imageUrl: typeof g.imageUrl === "string" ? g.imageUrl : undefined,
          createdAt: g.created_at,
          projectId: g.project_id ?? null,
          campaignId: g.campaign_id ?? null,
          resolution: rk,
          engine: g.engine ?? null,
          costCredits: typeof g.cost_credits === "number" ? g.cost_credits : null,
          aspectRatio: g.aspect_ratio ?? null,
        };
      });

      setResults(mapped);

      if (mainId && !mapped.some((x) => x.id === mainId)) setMainId(null);
    } catch (e) {
      setResults([]);
      setHistoryError(e instanceof Error ? friendlyApiError(e.message) : "Error cargando historial.");
    } finally {
      setHistoryLoading(false);
    }
  },
  [user, getAccessToken, selectedProjectId, mainId]
);

useEffect(() => {
  if (!user) return;
  loadGenerations("none");
  // eslint-disable-next-line react-hooks/exhaustive-deps
}, [user]);

useEffect(() => {
  if (!user) return;
  loadGenerations(selectedProjectId);
}, [selectedProjectId, loadGenerations, user]);

  // ─────────────────────────────────────────────
  // ✅ Options for selects
  // ─────────────────────────────────────────────
  const projectOptions = useMemo(() => {
    return [
      { value: "none", label: "Sin proyecto (Inbox)" },
      { value: "__create__", label: "+ Crear nuevo proyecto…" },
      ...projects.map((p) => ({ value: p.id, label: p.name })),
    ];
  }, [projects]);

  const campaignOptions = useMemo(() => {
    const opts: Array<{ value: string; label: string; disabled?: boolean }> = [{ value: "none", label: "Sin campaña" }];
    for (const c of campaigns) opts.push({ value: c.id, label: c.name });
    if (allowAdvanced && selectedProjectId !== "none") opts.push({ value: "__create_campaign__", label: "+ Crear campaña…" });
    return opts;
  }, [campaigns, allowAdvanced, selectedProjectId]);

  const currentCost = useMemo(() => {
    return costCreditsForResolution(userPlan, resolution) * 1;
  }, [userPlan, resolution]);

  // ─────────────────────────────────────────────
  // ✅ Generate
  // ─────────────────────────────────────────────
  const handleGenerate = async (e: FormEvent) => {
    e.preventDefault();

    if (!user) {
      goLoginWithNext(`/brand/create?preset=${presetId}`);
      return;
    }

    if (!prompt.trim()) return;

    if (!allowedResolutions.includes(resolution)) {
      openUpgrade({
        title: `Desbloquear calidad ${resolution}`,
        requiredPlan: getMinPlanForResolution(resolution),
        feature: "resolution",
        bullets: [`Tu plan actual no permite ${resolution}.`, `Mínimo requerido: ${prettyPlan(getMinPlanForResolution(resolution))}.`],
        from: `/brand/create?preset=${presetId}`,
      });
      return;
    }

    if (credits === null) return;

    if (credits < currentCost) {
      openUpgrade({
        title: "Créditos insuficientes",
        requiredPlan: userPlan === "free" ? "basic" : userPlan,
        feature: "credits",
        bullets: [`Necesitas ${currentCost} créditos y tienes ${credits}.`, "Compra créditos o baja la calidad."],
        from: `/brand/create?preset=${presetId}`,
      });
      return;
    }

    setIsGenerating(true);

    try {
      const token = await getAccessToken();
      if (!token) {
        goLoginWithNext(`/brand/create?preset=${presetId}`);
        return;
      }

      const projectIdToSend = selectedProjectId === "none" ? null : selectedProjectId;
      const campaignIdToSend =
        allowAdvanced && selectedProjectId !== "none" && selectedCampaignId !== "none" ? selectedCampaignId : null;

      const data = await api.generate(
        {
          prompt,
          style,
          aspectRatio,
          resolution,
          projectId: projectIdToSend,
          campaignId: campaignIdToSend,
          sourceMode: "brand",
        },
        { headers: { Authorization: `Bearer ${token}` } }
      );

      const typed = data as unknown as GenerateResponse;

      if (!typed || typed.success === false) {
        throw new Error(friendlyApiError((typed as any)?.error || "No se pudo generar la imagen."));
      }

      const imageUrlFromServer = typed.imageUrl || null;
      if (!imageUrlFromServer) throw new Error("El servidor no devolvió ninguna imagen.");

      const newItem: IAResult = {
        id: String(typed.generationId || cryptoId()),
        prompt,
        style,
        imageUrl: imageUrlFromServer,
        createdAt: new Date().toISOString(),
        projectId: projectIdToSend,
        campaignId: campaignIdToSend,
        resolution: normalizeResolutionKey(typed.resolution) ?? resolution,
        engine: typed.engine ?? null,
        costCredits: typeof (typed as any).costCredits === "number" ? (typed as any).costCredits : currentCost,
        aspectRatio: aspectRatio,
      };

      setResults((prev) => [newItem, ...prev]);
      setMainId(newItem.id);

      await loadGenerations(selectedProjectId);
    } catch (err) {
      console.error("Error generando:", err);

      if (isApiError(err) && err.status === 403) {
        openUpgrade({
          title: "Actualiza tu plan",
          requiredPlan: getMinPlanForResolution(resolution),
          feature: `resolution:${resolution}`,
          bullets: ["Tu plan actual no permite esta calidad."],
          from: `/brand/create?preset=${presetId}`,
        });
        return;
      }

      const msg = err instanceof Error ? friendlyApiError(err.message) : "Error generando imagen.";
      openUpgrade({
        title: "No se pudo generar",
        requiredPlan: userPlan,
        feature: "error_generate",
        bullets: [msg],
        from: `/brand/create?preset=${presetId}`,
      });
    } finally {
      setIsGenerating(false);
    }
  };

  // UI helpers (chips en selector)
  const Chip = ({ children }: { children: React.ReactNode }) => (
    <span className="text-[11px] px-2.5 py-1 rounded-full border border-white/10 bg-black/30 text-gray-200">{children}</span>
  );

  // ✅ Carrusel (selector)
  const scrollerRef = useRef<HTMLDivElement | null>(null);
  const strideRef = useRef<number>(360);
  const [activeIndex, setActiveIndex] = useState(0);
  const [canLeft, setCanLeft] = useState(false);
  const [canRight, setCanRight] = useState(true);

  const updateCarouselState = useCallback(() => {
    const el = scrollerRef.current;
    if (!el) return;

    const maxScroll = el.scrollWidth - el.clientWidth;
    const sl = el.scrollLeft;

    setCanLeft(sl > 2);
    setCanRight(sl < maxScroll - 2);

    const stride = strideRef.current || 1;
    const idx = Math.round(sl / stride);
    const clamped = Math.max(0, Math.min(BRAND_PRESETS_LIST.length - 1, idx));
    setActiveIndex(clamped);
  }, []);

  const measureStride = useCallback(() => {
    const el = scrollerRef.current;
    if (!el) return;

    const first = el.querySelector<HTMLElement>("[data-carousel-card='1']");
    if (!first) return;

    const firstRect = first.getBoundingClientRect();
    const second = first.nextElementSibling as HTMLElement | null;

    if (second) {
      const secondRect = second.getBoundingClientRect();
      const stride = Math.max(1, Math.round(secondRect.left - firstRect.left));
      strideRef.current = stride;
    } else {
      strideRef.current = Math.max(1, Math.round(firstRect.width + 20));
    }

    updateCarouselState();
  }, [updateCarouselState]);

  useEffect(() => {
    if (view !== "selector") return;
    const raf = requestAnimationFrame(() => measureStride());
    return () => cancelAnimationFrame(raf);
  }, [view, measureStride]);

  useEffect(() => {
    if (view !== "selector") return;
    const onResize = () => measureStride();
    window.addEventListener("resize", onResize);
    return () => window.removeEventListener("resize", onResize);
  }, [view, measureStride]);

  const scrollByCards = useCallback((dir: -1 | 1) => {
    const el = scrollerRef.current;
    if (!el) return;
    const stride = strideRef.current || 360;

    el.scrollTo({
      left: Math.max(0, el.scrollLeft + dir * stride),
      behavior: "smooth",
    });
  }, []);

  const goToIndex = useCallback((idx: number) => {
    const el = scrollerRef.current;
    if (!el) return;
    const stride = strideRef.current || 360;
    el.scrollTo({ left: idx * stride, behavior: "smooth" });
  }, []);

  /** ─────────────────────────────────────────────────────────────
   * ✅ VISTA 1: Selector
   * ───────────────────────────────────────────────────────────── */
  if (view === "selector") {
    return (
      <section className="max-w-5xl">
        <style>{`
          .no-scrollbar::-webkit-scrollbar { display: none; }
          .no-scrollbar { -ms-overflow-style: none; scrollbar-width: none; }
        `}</style>

        <div className="mb-8 flex flex-col md:flex-row md:items-end md:justify-between gap-4">
          <div>
            <div className="text-xs uppercase tracking-[0.25em] text-gray-400">Brand Mode</div>
            <h1 className="text-2xl md:text-3xl font-bold mt-2">Generador IA</h1>
            <p className="text-gray-300/90 mt-2 text-sm md:text-base max-w-2xl leading-relaxed">
              Elige el tipo de creación y luego completa tu brief para producir con consistencia.
            </p>

            <div className="mt-4 flex flex-wrap gap-2">
              <Chip>
                Plan: <span className="text-gray-100 font-medium">{prettyPlan(userPlan)}</span>
              </Chip>
              <Chip>
                Créditos: <span className="text-gray-100 font-medium">{credits === null ? "…" : String(credits)}</span>
              </Chip>
              <Chip>
                Brands: <span className="text-gray-100 font-medium">{brandsLimitLabel}</span>
              </Chip>
              <Chip>
                Persistencia: <span className="text-gray-100 font-medium">{yesNo(allowPersist)}</span>
              </Chip>
              <Chip>
                Campañas: <span className="text-gray-100 font-medium">{yesNo(allowAdvanced)}</span>
              </Chip>
            </div>

            {!user && (
              <div className="mt-4 text-[12px] text-amber-200/90">
                Estás en modo invitado. Inicia sesión para guardar proyectos, campañas y generar en tu historial.
              </div>
            )}
          </div>

          <div className="flex flex-wrap gap-2">
            <button
              onClick={goEditBrandKit}
              className="px-4 py-2 rounded-xl text-sm font-semibold border border-white/15 hover:bg-white/10 transition"
            >
              Editar Brand Kit
            </button>

            <button
              disabled
              className="px-4 py-2 rounded-xl text-sm font-semibold border border-white/10 bg-white/5 opacity-60 cursor-not-allowed"
              title="Próximamente"
            >
              📁 Proyectos (próx.)
            </button>

            {!user && (
              <button
                onClick={() => goLoginWithNext("/brand/create")}
                className="px-4 py-2 rounded-xl text-sm font-semibold
                  bg-gradient-to-r from-indigo-500 to-purple-500
                  hover:from-indigo-400 hover:to-purple-400
                  transition shadow-lg shadow-indigo-500/30"
              >
                Iniciar sesión
              </button>
            )}
          </div>
        </div>

        <div className="mb-4 flex items-end justify-between gap-4">
          <div>
            <h2 className="text-xl font-semibold">Selecciona un tipo de creación</h2>
            <p className="text-sm text-gray-400 mt-1">Usa las flechas para ver todas las opciones (sin bajar en la página).</p>
          </div>

          <div className="hidden sm:flex items-center gap-3">
            <div className="text-[12px] text-gray-400">
              <span className="text-gray-200 font-semibold">{activeIndex + 1}</span>
              <span className="text-gray-500"> / {BRAND_PRESETS_LIST.length}</span>
            </div>

            <div className="flex items-center gap-1.5">
              {BRAND_PRESETS_LIST.map((_, i) => (
                <button
                  key={`dot_${i}`}
                  type="button"
                  onClick={() => goToIndex(i)}
                  className={`h-1.5 rounded-full transition ${i === activeIndex ? "w-6 bg-white/60" : "w-3 bg-white/15 hover:bg-white/25"}`}
                  title={`Ir a ${i + 1}`}
                />
              ))}
            </div>
          </div>
        </div>

        <div className="flex items-stretch gap-3">
          <button
            type="button"
            onClick={() => scrollByCards(-1)}
            disabled={!canLeft}
            className="hidden sm:flex w-12 shrink-0 items-center justify-center rounded-2xl border border-white/10 bg-white/5 hover:bg-white/10 transition disabled:opacity-40 disabled:cursor-not-allowed"
            title="Anterior"
            aria-label="Anterior"
          >
            <span className="text-xl text-gray-200">‹</span>
          </button>

          <div
            ref={scrollerRef}
            onScroll={updateCarouselState}
            className="flex-1 overflow-x-auto scroll-smooth snap-x snap-mandatory no-scrollbar"
            style={{ WebkitOverflowScrolling: "touch" }}
          >
            <div className="flex gap-5 pr-2">
              {BRAND_PRESETS_LIST.map((p: any, idx: number) => (
                <button
                  key={p.id}
                  data-carousel-card={idx === 0 ? "1" : undefined}
                  onClick={() => navigate(`/brand/create?preset=${encodeURIComponent(p.id)}`)}
                  className="
                    relative text-left rounded-2xl border border-white/10 bg-white/5 hover:bg-white/10 transition p-6
                    snap-start
                    min-w-[85%] sm:min-w-[320px] lg:min-w-[320px]
                  "
                  title={p.title}
                >
                  <div className="flex items-start justify-between gap-4">
                    <div>
                      <div className="text-xs uppercase tracking-[0.25em] text-gray-400">{p.title}</div>
                      <h3 className="mt-2 text-lg font-semibold">
                        {p.icon} {p.title}
                      </h3>
                      <p className="mt-2 text-sm text-gray-400 leading-relaxed">{p.subtitle}</p>
                    </div>

                    <span className="text-[11px] px-2 py-1 rounded-full border border-indigo-400/20 bg-indigo-500/10 text-indigo-200">
                      {p.recommendedAspectRatio}
                    </span>
                  </div>

                  <div className="mt-5 text-xs text-indigo-300">Continuar →</div>
                </button>
              ))}
            </div>
          </div>

          <button
            type="button"
            onClick={() => scrollByCards(1)}
            disabled={!canRight}
            className="hidden sm:flex w-12 shrink-0 items-center justify-center rounded-2xl border border-white/10 bg-white/5 hover:bg-white/10 transition disabled:opacity-40 disabled:cursor-not-allowed"
            title="Siguiente"
            aria-label="Siguiente"
          >
            <span className="text-xl text-gray-200">›</span>
          </button>
        </div>

        <div className="sm:hidden mt-4 flex items-center justify-between">
          <div className="text-[12px] text-gray-400">
            <span className="text-gray-200 font-semibold">{activeIndex + 1}</span>
            <span className="text-gray-500"> / {BRAND_PRESETS_LIST.length}</span>
          </div>

          <div className="flex items-center gap-2">
            <button
              type="button"
              onClick={() => scrollByCards(-1)}
              disabled={!canLeft}
              className="px-3 py-2 rounded-xl border border-white/10 bg-white/5 hover:bg-white/10 transition disabled:opacity-40 disabled:cursor-not-allowed"
              aria-label="Anterior"
            >
              ‹
            </button>
            <button
              type="button"
              onClick={() => scrollByCards(1)}
              disabled={!canRight}
              className="px-3 py-2 rounded-xl border border-white/10 bg-white/5 hover:bg-white/10 transition disabled:opacity-40 disabled:cursor-not-allowed"
              aria-label="Siguiente"
            >
              ›
            </button>
          </div>
        </div>

        <UpgradeModal
          open={upgradeOpen && !!upgradeCfg}
          onClose={() => setUpgradeOpen(false)}
          title={upgradeCfg?.title || "Actualizar plan"}
          requiredPlan={upgradeCfg?.requiredPlan || "basic"}
          feature={upgradeCfg?.feature || "brand_persist"}
          from={upgradeCfg?.from || "/brand/create"}
          bullets={upgradeCfg?.bullets || []}
        />
      </section>
    );
  }

  /** ─────────────────────────────────────────────────────────────
   * ✅ VISTA 2: Preset inválido
   * ───────────────────────────────────────────────────────────── */
  if (view === "invalid") {
    return (
      <section className="max-w-5xl">
        <div className="max-w-md text-center rounded-2xl border border-white/10 bg-white/5 p-8">
          <h1 className="text-2xl font-bold">Preset inválido</h1>
          <p className="text-sm text-gray-400 mt-2">
            No existe el preset: <span className="text-gray-200">{presetId}</span>
          </p>
          <button
            onClick={() => navigate("/brand/create")}
            className="mt-5 px-4 py-2 rounded-lg border border-white/10 hover:bg-white/10 transition text-sm"
          >
            Volver al selector
          </button>
        </div>
      </section>
    );
  }

  /** ─────────────────────────────────────────────────────────────
   * ✅ VISTA 3: Formulario
   * ───────────────────────────────────────────────────────────── */
  if (!preset) return null;

  const showResolutionWarning = !isResolutionAllowed(userPlan, resolution);

  return (
    <section className="max-w-6xl mx-auto space-y-4">
      {/* ✅ Limpio arriba: link mini para volver */}
      <div className="px-1">
        <button
          type="button"
          onClick={() => navigate("/brand/create")}
          className="text-[12px] text-indigo-300 hover:text-indigo-200 underline underline-offset-4"
          title="Cambiar tipo de creación"
        >
          Cambiar tipo de creación
        </button>
      </div>

      <div className="rounded-3xl border border-white/10 bg-white/5 backdrop-blur-md shadow-2xl shadow-indigo-900/35">
        {/* PREVIEW */}
        <div className="relative rounded-t-3xl overflow-hidden">
          <div className="w-full bg-black/40 h-[40vh] md:h-[52vh] max-h-[560px] min-h-[300px]">
            {mainItem?.imageUrl ? (
              <img
                src={mainItem.imageUrl}
                alt=""
                draggable={false}
                onLoad={() => setMainImgLoaded(true)}
                className={[
                  "w-full h-full object-cover",
                  "transition-all duration-500 ease-out",
                  mainImgLoaded ? "opacity-100 blur-0 scale-100" : "opacity-0 blur-md scale-[1.01]",
                  isGenerating ? "opacity-70 blur-[2px]" : "",
                ].join(" ")}
              />
            ) : (
              <div className="w-full h-full relative">
                <NoisePlaceholder />
                {!isGenerating && (
                  <div className="absolute inset-0 flex items-center justify-center">
                    <div className="text-center px-6">
                      <div className="mx-auto w-12 h-12 rounded-2xl border border-white/10 bg-black/40 backdrop-blur flex items-center justify-center text-xl">
                        ✨
                      </div>
                      <div className="mt-4 text-base font-semibold text-gray-100">Tu creación aparecerá aquí</div>
                      <div className="mt-2 text-sm text-gray-300/80 max-w-md mx-auto">
                        Completa el brief, ajusta controles y presiona{" "}
                        <span className="text-gray-100 font-semibold">Generar</span>.
                      </div>
                      {results.length > 0 && (
                        <div className="mt-3 text-[11px] text-gray-400">Tip: también puedes seleccionar una miniatura abajo.</div>
                      )}
                    </div>
                  </div>
                )}
              </div>
            )}

            {isGenerating && (
              <div className="absolute inset-0">
                <div className="absolute inset-0 bg-black/35" />
                <div className="absolute inset-0 flex items-center justify-center">
                  <div className="rounded-2xl border border-white/10 bg-black/50 backdrop-blur px-5 py-3">
                    <div className="text-sm font-semibold">Generando…</div>
                    <div className="text-[11px] text-gray-300 mt-1">Esto puede tardar unos segundos.</div>
                  </div>
                </div>
              </div>
            )}

            {mainItem && (
              <button
                type="button"
                onClick={() => openPreview(mainItem)}
                className="absolute top-4 right-4 text-[11px] px-3 py-2 rounded-xl border border-white/15 bg-black/35 hover:bg-white/10 transition backdrop-blur"
              >
                Abrir preview ↗
              </button>
            )}
          </div>
        </div>

        <form onSubmit={handleGenerate} className="p-4 md:p-5 space-y-4">
          {/* BRIEF */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
            <div>
              <label className="block text-xs text-gray-400 mb-2">Objetivo</label>
              <input
                value={goal}
                onChange={(e) => setGoal(e.target.value)}
                placeholder="Ej: vender, leads, awareness…"
                className="w-full h-10 px-4 rounded-xl text-sm bg-black/30 border border-white/10 text-gray-100 outline-none focus:ring-2 focus:ring-indigo-500/40 focus:border-indigo-400/40"
              />
            </div>
            <div>
              <label className="block text-xs text-gray-400 mb-2">Producto / tema</label>
              <input
                value={productOrTopic}
                onChange={(e) => setProductOrTopic(e.target.value)}
                placeholder="Ej: proteína premium, SaaS…"
                className="w-full h-10 px-4 rounded-xl text-sm bg-black/30 border border-white/10 text-gray-100 outline-none focus:ring-2 focus:ring-indigo-500/40 focus:border-indigo-400/40"
              />
            </div>
            <div>
              <label className="block text-xs text-gray-400 mb-2">Audiencia</label>
              <input
                value={audience}
                onChange={(e) => setAudience(e.target.value)}
                placeholder="Ej: mujeres 25-35, emprendedores…"
                className="w-full h-10 px-4 rounded-xl text-sm bg-black/30 border border-white/10 text-gray-100 outline-none focus:ring-2 focus:ring-indigo-500/40 focus:border-indigo-400/40"
              />
            </div>
          </div>

          {showResolutionWarning && (
            <div className="text-[12px] text-red-300">
              Tu plan no permite <span className="text-red-200 font-semibold">{resolution}</span>. Mínimo requerido:{" "}
              <span className="text-red-200 font-semibold">{prettyPlan(getMinPlanForResolution(resolution))}</span>.
            </div>
          )}

          {/* ONE-LINE controls */}
          <div className="flex items-end gap-2">
            <div className="flex-1 grid grid-cols-1 md:grid-cols-5 gap-3">
              <div className="flex flex-col gap-1">
                <PillLabel>Proyecto</PillLabel>
                <PillSelect
                  value={selectedProjectId}
                  onChange={(v) => {
                    if (v === "__create__") {
                      if (!user) {
                        goLoginWithNext(`/brand/create?preset=${presetId}`);
                        return;
                      }
                      setCreateProjectOpen(true);
                      return;
                    }
                    setSelectedProjectId(v);
                    setSelectedCampaignId("none");
                  }}
                  options={projectOptions}
                />
              </div>

              <div className="flex flex-col gap-1">
                <div className="relative h-4 flex items-center">
                  <PillLabel>Campaña</PillLabel>

                  {!allowAdvanced && (
                    <button
                      type="button"
                      onClick={openUpgradeAdvanced}
                      className="
                        absolute right-0 top-1/2 -translate-y-1/2
                        text-[10px] px-2 py-0.5 rounded-lg
                        border border-indigo-400/25 bg-indigo-500/10 hover:bg-indigo-500/15
                        transition text-indigo-200 leading-none
                      "
                    >
                      Desbloquear
                    </button>
                  )}
                </div>

                <PillSelect
                  value={selectedCampaignId}
                  disabled={!allowAdvanced || selectedProjectId === "none"}
                  onChange={(v) => {
                    if (v === "__create_campaign__") {
                      if (!allowAdvanced) return;
                      setCreateCampaignOpen(true);
                      return;
                    }
                    setSelectedCampaignId(v);
                  }}
                  options={campaignOptions}
                />
              </div>

              <div className="flex flex-col gap-1">
                <PillLabel>Estilo</PillLabel>
                <PillSelect
                  value={style}
                  onChange={setStyle}
                  options={[
                    { value: "Realista", label: "Realista" },
                    { value: "Ilustración", label: "Ilustración" },
                    { value: "Minimal", label: "Minimal" },
                    { value: "Futurista", label: "Futurista" },
                    { value: "Anime", label: "Anime" },
                    { value: "Vintage", label: "Vintage" },
                    { value: "Logo corporativo", label: "Logo corporativo" },
                    { value: "Portada épica", label: "Portada épica" },
                  ]}
                />
              </div>

              <div className="flex flex-col gap-1">
                <PillLabel>Tamaño</PillLabel>
                {fixedAspectRatio ? (
                  <PillSelect
                    value={aspectRatio}
                    disabled
                    onChange={() => {}}
                    options={[{ value: fixedAspectRatio, label: fixedAspectRatio }]}
                    title="Este formato tiene tamaño fijo."
                  />
                ) : (
                  <PillSelect
                    value={aspectRatio}
                    onChange={setAspectRatio}
                    options={ASPECT_RATIOS.map((a) => ({ value: a.value, label: a.label }))}
                  />
                )}
              </div>

              <div className="flex flex-col gap-1">
                <PillLabel>Calidad</PillLabel>
                <PillSelect
                  value={resolution}
                  title={`Export: ${getExportSizeLabel(resolution)}`}
                  onChange={(val) => {
                    const next = val as ResolutionKey;

                    if (!allowedResolutions.includes(next)) {
                      openUpgrade({
                        title: `Desbloquear calidad ${next}`,
                        requiredPlan: getMinPlanForResolution(next),
                        feature: "resolution",
                        bullets: [`Tu plan actual no permite ${next}.`, `Mínimo requerido: ${prettyPlan(getMinPlanForResolution(next))}.`],
                        from: `/brand/create?preset=${presetId}`,
                      });
                      return;
                    }

                    setResolution(next);
                  }}
                  options={ALL_RESOLUTIONS.map((r) => ({
                    value: r,
                    label: r.toUpperCase(),
                    disabled: !allowedResolutions.includes(r),
                  }))}
                />
              </div>
            </div>

            {/* ✅ REFRESCAR A LA DERECHA */}
            <div className="shrink-0 flex flex-col items-end gap-2">
              <button
                type="button"
                onClick={() => {
                  if (!user) {
                    goLoginWithNext(`/brand/create?preset=${presetId}`);
                    return;
                  }
                  loadProjects();
                }}
                className="h-10 text-[11px] px-3 rounded-xl border border-white/10 hover:bg-white/10 transition text-gray-200"
                title="Refrescar proyectos"
              >
                🔄 Refrescar
              </button>

              <div className="hidden md:block text-[11px] text-gray-500 min-w-[170px] text-right">
                {projectsLoading
                  ? "Cargando…"
                  : projectsError
                  ? <span className="text-red-300">{projectsError}</span>
                  : needsUpscaleToBeReal(resolution)
                  ? "Ideal para export final."
                  : "Ideal para iterar rápido."}
              </div>
            </div>
          </div>

          {/* Prompt bar */}
          <div className="flex flex-col gap-2">
            <div
              className="
                flex items-center gap-2
                rounded-2xl border border-white/15 bg-black/25
                px-3 py-2
                hover:border-white/25 transition
              "
            >
              <input
                id="prompt"
                name="prompt"
                value={prompt}
                onChange={(e) => setPrompt(e.target.value)}
                placeholder="Escribe tu prompt aquí…"
                className="
                  flex-1 h-10 px-3 rounded-xl text-sm
                  bg-[#0c0c14] hover:bg-[#121225]
                  border border-white/15 hover:border-white/25
                  text-gray-100 placeholder:text-gray-500
                  outline-none transition
                  focus:ring-2 focus:ring-indigo-500/40 focus:border-indigo-400/40
                "
              />

              <button
                type="button"
                onClick={() => {
                  if (!canContinueBrief) return;
                  setPrompt(basePrompt);
                }}
                disabled={!canContinueBrief}
                className="
                  h-10 px-4 rounded-xl text-sm font-semibold whitespace-nowrap
                  border border-white/15 hover:bg-white/10 transition
                  disabled:opacity-50 disabled:cursor-not-allowed
                "
                title={!canContinueBrief ? "Completa objetivo, producto y audiencia para construir el prompt" : "Rearmar prompt con Brand Kit"}
              >
                Re-armar
              </button>

              <button
                type="submit"
                disabled={isGenerating || !prompt.trim() || !canContinueBrief || credits === null || (credits !== null && credits < currentCost)}
                className="
                  h-10 px-5 rounded-xl text-sm font-semibold inline-flex items-center gap-2 whitespace-nowrap
                  bg-white text-black hover:bg-gray-200
                  shadow-lg shadow-black/25
                  disabled:opacity-50 disabled:cursor-not-allowed
                  transition
                  active:scale-[0.98] transition-transform
                "
                title={!canContinueBrief ? "Completa el brief (objetivo/producto/audiencia)" : "Generar"}
              >
                {isGenerating ? "Generando…" : "Generar"}
                {!isGenerating && <span className="text-xs font-bold">→</span>}
              </button>
            </div>

            {/* mini meta bar */}
            <div className="flex flex-wrap items-center gap-2 text-[11px] text-gray-400">
              <span className="px-2 py-1 rounded-full bg-black/30 border border-white/10">
                Coste: <span className="text-gray-200 font-semibold">{currentCost}</span> crédito{currentCost === 1 ? "" : "s"}
              </span>

              <span className="px-2 py-1 rounded-full bg-black/30 border border-white/10">
                {resolution.toUpperCase()} · {getExportSizeLabel(resolution)}
              </span>

              <span className="px-2 py-1 rounded-full bg-black/30 border border-white/10">{style}</span>
              <span className="px-2 py-1 rounded-full bg-black/30 border border-white/10">{aspectRatio}</span>

              {needsUpscaleToBeReal(resolution) && (
                <span className="px-2 py-1 rounded-full bg-indigo-500/10 border border-indigo-400/20 text-indigo-200">
                  Export final (upscale)
                </span>
              )}

              {!allowPersist && (
                <span className="px-2 py-1 rounded-full bg-amber-500/10 border border-amber-400/20 text-amber-200">
                  Sin persistencia (Basic+)
                </span>
              )}
            </div>
          </div>
        </form>
      </div>

      {/* HISTORIAL */}
      <div className="rounded-3xl border border-white/10 bg-white/5 backdrop-blur-md p-4 md:p-5">
        <div className="flex items-center justify-between gap-3">
          <div className="text-sm font-semibold">Historial</div>
          <div className="flex items-center gap-2">
            <button
              type="button"
              onClick={() => loadGenerations(selectedProjectId)}
              className="text-[11px] px-3 py-1.5 rounded-lg border border-white/10 hover:bg-white/10 transition text-gray-200"
            >
              🔄 Refrescar
            </button>
            <div className="text-[11px] text-gray-500">{historyLoading ? "Cargando…" : `${results.length} elemento(s)`}</div>
          </div>
        </div>

        {!!historyError && <div className="mt-3 text-sm text-red-300">{historyError}</div>}

        <div className="mt-4 grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-6 gap-3">
          {results.map((item) => (
            <button
              key={item.id}
              onClick={() => setMainId(item.id)}
              onDoubleClick={() => openPreview(item)}
              className={`
                text-left rounded-2xl border overflow-hidden transition
                ${mainId === item.id ? "border-indigo-400/60 bg-indigo-500/10" : "border-white/10 bg-black/25 hover:bg-white/5 hover:border-indigo-400/30"}
              `}
              title={item.prompt}
            >
              <div className="relative">
                {item.imageUrl ? (
                  <img src={item.imageUrl} alt={item.prompt} className="w-full aspect-video object-cover" draggable={false} />
                ) : (
                  <div className="aspect-video bg-gradient-to-br from-indigo-800/40 via-purple-800/40 to-slate-900/60" />
                )}

                <div className="absolute top-2 left-2 text-[10px] px-2 py-1 rounded-full bg-black/55 border border-white/10 text-gray-100">
                  {(item.resolution || "—").toUpperCase()}
                </div>

                <div className="absolute top-2 right-2 text-[10px] px-2 py-1 rounded-full bg-black/55 border border-white/10 text-gray-100">
                  {item.costCredits ? `${item.costCredits}c` : "—"}
                </div>
              </div>

              <div className="p-2.5">
                <div className="text-[11px] text-gray-200 truncate">{item.prompt}</div>
                <div className="mt-1 text-[10px] text-gray-400 truncate">
                  {item.style} · {item.aspectRatio || "—"}
                </div>

                <div className="mt-2 flex items-center justify-between gap-2">
                  <button
                    type="button"
                    onClick={(e) => {
                      e.stopPropagation();
                      openPreview(item);
                    }}
                    className="text-[10px] px-2 py-1 rounded-lg border border-white/10 hover:bg-white/10 transition text-gray-200"
                  >
                    Abrir ↗
                  </button>
                  <span className="text-[10px] text-gray-500">{item.createdAt ? new Date(item.createdAt).toLocaleDateString() : "—"}</span>
                </div>
              </div>
            </button>
          ))}

          {!historyLoading && results.length === 0 && (
            <div className="col-span-full text-center text-gray-500 text-sm py-10">
              <div className="text-gray-300 font-semibold">Aún no hay resultados</div>
              <div className="mt-2 text-gray-500">Completa el brief y presiona Generar.</div>
              <div className="mt-5">
                <button
                  type="button"
                  onClick={() => {
                    const el = document.getElementById("prompt") as HTMLInputElement | null;
                    el?.scrollIntoView({ behavior: "smooth", block: "center" });
                    el?.focus?.();
                  }}
                  className="text-xs px-4 py-2 rounded-xl border border-white/15 hover:bg-white/10 transition text-gray-200"
                >
                  Escribir prompt →
                </button>
              </div>
            </div>
          )}
        </div>

        <div className="mt-3 text-[11px] text-gray-500">Tip: Click = seleccionar para preview central · Doble click o “Abrir” = preview completo.</div>
      </div>

      {/* Modal crear proyecto */}
      {createProjectOpen && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/70 px-4" onClick={() => setCreateProjectOpen(false)}>
          <div className="w-full max-w-md rounded-2xl border border-white/10 bg-[#0c0c14] p-6 shadow-2xl" onClick={(e) => e.stopPropagation()}>
            <div className="text-lg font-bold text-white">Crear proyecto</div>
            <p className="text-sm text-gray-400 mt-1">Se guardará en tu cuenta.</p>

            <input
              value={projectName}
              onChange={(e) => setProjectName(e.target.value)}
              placeholder="Nombre del proyecto"
              className="mt-4 w-full px-4 py-3 rounded-xl bg-black/30 border border-white/10 text-gray-100 outline-none focus:ring-2 focus:ring-indigo-500/40"
            />

            <div className="mt-5 flex justify-end gap-2">
              <button
                type="button"
                onClick={() => setCreateProjectOpen(false)}
                className="px-4 py-2 rounded-xl border border-white/10 hover:bg-white/10 transition text-sm"
              >
                Cancelar
              </button>
              <button
                type="button"
                onClick={createProject}
                className="px-4 py-2 rounded-xl text-sm font-semibold bg-indigo-500/15 border border-indigo-400/25 hover:bg-indigo-500/20 transition"
              >
                Crear
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Modal crear campaña */}
      {createCampaignOpen && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/70 px-4" onClick={() => setCreateCampaignOpen(false)}>
          <div className="w-full max-w-md rounded-2xl border border-white/10 bg-[#0c0c14] p-6 shadow-2xl" onClick={(e) => e.stopPropagation()}>
            <div className="text-lg font-bold text-white">Crear campaña</div>
            <p className="text-sm text-gray-400 mt-1">Quedará guardada localmente para este proyecto.</p>

            <input
              value={campaignName}
              onChange={(e) => setCampaignName(e.target.value)}
              placeholder="Nombre de campaña"
              className="mt-4 w-full px-4 py-3 rounded-xl bg-black/30 border border-white/10 text-gray-100 outline-none focus:ring-2 focus:ring-indigo-500/40"
            />

            <div className="mt-5 flex justify-end gap-2">
              <button
                type="button"
                onClick={() => setCreateCampaignOpen(false)}
                className="px-4 py-2 rounded-xl border border-white/10 hover:bg-white/10 transition text-sm"
              >
                Cancelar
              </button>
              <button
                type="button"
                onClick={createCampaign}
                className="px-4 py-2 rounded-xl text-sm font-semibold bg-indigo-500/15 border border-indigo-400/25 hover:bg-indigo-500/20 transition"
              >
                Crear
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Modal preview */}
      {previewOpen && previewItem && (
        <div className="fixed inset-0 z-50 bg-black/80 px-4 py-6 overflow-y-auto" onClick={closePreview}>
          <div
            className="
              w-full max-w-5xl mx-auto
              rounded-2xl border border-white/10 bg-[#0c0c14] shadow-2xl overflow-hidden
              flex flex-col
              max-h-[90vh]
            "
            onClick={(e) => e.stopPropagation()}
          >
            <div className="p-4 border-b border-white/10 flex items-start justify-between gap-4 shrink-0">
              <div className="min-w-0">
                <div className="text-sm font-semibold text-white truncate">{previewItem.prompt}</div>

                <div className="mt-1 text-[11px] text-gray-400 flex flex-wrap gap-2 items-center">
                  <span className="px-2 py-1 rounded-full bg-black/40 border border-white/10">
                    {(previewItem.resolution || "—").toUpperCase()} · Export{" "}
                    {getExportSizeLabel((previewItem.resolution as ResolutionKey) || "720p")}
                  </span>
                  <span className="px-2 py-1 rounded-full bg-black/40 border border-white/10">{prettyEngine(previewItem.engine)}</span>
                  <span className="px-2 py-1 rounded-full bg-black/40 border border-white/10">{previewItem.style}</span>
                  <span className="px-2 py-1 rounded-full bg-black/40 border border-white/10">
                    {previewItem.costCredits ? `${previewItem.costCredits} créditos` : "—"}
                  </span>
                </div>

                {needsUpscaleToBeReal((previewItem.resolution as ResolutionKey) || "720p") && (
                  <div className="mt-2 text-[11px] text-indigo-200">
                    Esta exportación incluye upscale automático (Real-ESRGAN) — haz zoom para notar nitidez en bordes.
                  </div>
                )}
              </div>

              <div className="flex items-center gap-2 shrink-0">
                {previewItem.imageUrl && (
                  <a
                    href={previewItem.imageUrl}
                    target="_blank"
                    rel="noreferrer"
                    className="text-[11px] px-3 py-2 rounded-lg border border-white/10 hover:bg-white/10 transition text-gray-200"
                    title="Abrir imagen en nueva pestaña"
                  >
                    ↗ Abrir
                  </a>
                )}
                <button
                  onClick={closePreview}
                  className="text-[11px] px-3 py-2 rounded-lg border border-white/10 hover:bg-white/10 transition text-gray-200"
                >
                  ✕ Cerrar
                </button>
              </div>
            </div>

            <div className="p-4 overflow-y-auto">
              <div className="rounded-2xl border border-white/10 bg-black/40 overflow-auto">
                {previewItem.imageUrl ? (
                  <img src={previewItem.imageUrl} alt={previewItem.prompt} className="w-full h-auto block" draggable={false} />
                ) : (
                  <div className="h-80 bg-gradient-to-br from-indigo-800/40 via-purple-800/40 to-slate-900/60" />
                )}
              </div>

              <div className="mt-3 text-[11px] text-gray-500">
                Tip: para notar diferencia entre 1080p vs 4K/8K, abre la imagen en nueva pestaña y haz zoom al 200–300%.
              </div>
            </div>
          </div>
        </div>
      )}

      <UpgradeModal
        open={upgradeOpen && !!upgradeCfg}
        onClose={() => setUpgradeOpen(false)}
        title={upgradeCfg?.title || "Actualizar plan"}
        requiredPlan={upgradeCfg?.requiredPlan || "basic"}
        feature={upgradeCfg?.feature || "brand_persist"}
        from={upgradeCfg?.from || "/brand/create"}
        bullets={upgradeCfg?.bullets || []}
      />
    </section>
  );
}


